import { useMemo } from "react";
import type { FacilityNode } from "@arc/contracts";
import {
  bypassHint,
  canMoveSource,
  canManageSubtree,
  archivedSubtreeRootIds,
} from "./facility-tree.js";

/**
 * The caller's best-effort facility-permission gates, derived from the flat permitted tree — the CLIENT MIRRORS of the
 * server's authz (ADR-004/005/006/008/009). Extracted VERBATIM from the classic `FacilitySelection` so BOTH navigators
 * (classic master-detail + the drill-down) gate identically off one source of truth; the server stays authoritative.
 *
 * Pure derivation (no session, no side effects): every memo keys on `flat` (+ `bypass`), so the returned Sets are stable
 * across renders until the tree changes — safe to spread into consumer memo/effect deps. Browse-only concerns (expand
 * state, selection, reveal-scroll) live in each navigator, NOT here.
 */
export interface FacilityAuthz {
  /** The caller is a realm-wide admin (bypasses per-node grants) — inferred from the returned tree (ADR-005). */
  bypass: boolean;
  /** Nodes the caller can MOVE (source-side authz mirror, ADR-005) — drives the drag handle + "Move to…". */
  movableIds: ReadonlySet<number>;
  /** Nodes under which the caller can ADD a child (manage on the parent, or bypass) — drives create affordances. */
  canAddIds: ReadonlySet<number>;
  /** Nodes with subtree-wide manage (mirror of the server's requireManageSubtree) — gates Archive/Unarchive. */
  manageSubtreeIds: ReadonlySet<number>;
  /** Nodes the caller can DUPLICATE (ADR-008): subtree-manage AND create-authz on the destination parent. */
  canDuplicateIds: ReadonlySet<number>;
  /** Nodes the caller can INSERT ABOVE (ADR-009): a sibling = a child of the (canonical, active) parent. */
  canInsertAboveIds: ReadonlySet<number>;
  /** Archived node ids (the full tree) — the Archived filter + the "don't target an archived parent" guards. */
  archivedIds: ReadonlySet<number>;
  /** The TOP of each archived subtree — only these carry Unarchive (a descendant can't restore under an archived root). */
  archivedRootIds: ReadonlySet<number>;
}

export function useFacilityAuthz(flat: readonly FacilityNode[]): FacilityAuthz {
  const bypass = useMemo(() => bypassHint(flat), [flat]);
  // Which nodes the caller can move (best-effort mirror of ADR-005 source-side authz) — drives per-row affordances.
  const movableIds = useMemo(
    () => new Set(flat.filter((f) => canMoveSource(f, flat, bypass)).map((f) => f.id)),
    [flat, bypass],
  );
  // Where the caller can add a child (create authz = manage on the parent, or bypass) — drives the hover "+".
  const canAddIds = useMemo(
    () =>
      new Set(
        flat
          .filter((f) => bypass || f.permission === "manage" || f.permission === "access_manage")
          .map((f) => f.id),
      ),
    [flat, bypass],
  );
  // Where the caller has subtree-wide manage (mirror of the server's requireManageSubtree) — gates Archive/Unarchive.
  const manageSubtreeIds = useMemo(
    () => new Set(flat.filter((f) => canManageSubtree(f, flat, bypass)).map((f) => f.id)),
    [flat, bypass],
  );
  // Where the caller can DUPLICATE (ADR-008 mirror): subtree-wide manage AND create-authz on the destination parent
  // (the copy's parent = the source's parent) — a ROOT source is bypass-only (like root create). Wider gate than
  // Archive/Delete, which don't create a sibling (panel).
  const canDuplicateIds = useMemo(
    () =>
      new Set(
        flat
          .filter(
            (f) =>
              manageSubtreeIds.has(f.id) &&
              (bypass || (f.parentId !== null && canAddIds.has(f.parentId))),
          )
          .map((f) => f.id),
      ),
    [flat, bypass, manageSubtreeIds, canAddIds],
  );
  const archivedIds = useMemo(() => new Set(flat.filter((f) => f.archived).map((f) => f.id)), [flat]);
  // Only the TOP of each archived subtree carries the Unarchive action — a descendant of an archived node can't be
  // restored while its ancestor is archived (the server 409s), so its Unarchive button is suppressed.
  const archivedRootIds = useMemo(() => archivedSubtreeRootIds(flat), [flat]);
  // Where the caller can INSERT ABOVE (ADR-009): create a SIBLING = a child of the node's parent. Parent-derived create
  // authz (`canAddIds.has(parentId)`, mirrors `create()`), disabled on roots (no parent), and — crucially — computed
  // from the CANONICAL `flat` with an ACTIVE-parent predicate (`!archivedIds.has(parentId)` mirrors the server's
  // archived-parent guard; `excludeArchived` HOISTS a row's `parentId`, so gating off the rendered row would misplace
  // the sibling or false-green on an archived parent — panel consensus).
  const canInsertAboveIds = useMemo(
    () =>
      new Set(
        flat
          .filter((f) => f.parentId !== null && !archivedIds.has(f.parentId) && canAddIds.has(f.parentId))
          .map((f) => f.id),
      ),
    [flat, archivedIds, canAddIds],
  );

  return {
    bypass,
    movableIds,
    canAddIds,
    manageSubtreeIds,
    canDuplicateIds,
    canInsertAboveIds,
    archivedIds,
    archivedRootIds,
  };
}
